import React, { ReactNode, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import { useAuthContext } from '@/hooks/AuthContext'
import { useRequest } from '@/hooks/useSwr'
import { BASE_URL } from '../../env'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import Head from '@/components/common/layout/Head'
import ProtectedPage from '@/components/common/layout/ProtectedPage'
import PhoneNumberModal from '@/components/common/modal/PhoneNumberModal'
import Logo from '@/components/common/logo/Logo'
import { ToastContainer } from 'react-toastify'
import {
  SlCreditCard,
  SlCamrecorder,
  SlLogout,
  SlNote,
  SlPlaylist,
  SlUser,
  SlCalender,
  SlNotebook,
  SlRocket,
} from 'react-icons/sl'
import {
  MdInstallDesktop,
  MdVideoLibrary,
  MdHighlight,
  MdOutlineTranscribe,
} from 'react-icons/md'
import { FaChalkboardTeacher } from 'react-icons/fa'
import {
  ArrowUpRight,
  Calendar,
  ChevronRight,
  Clock3,
  Copy,
  CreditCard,
  ExternalLink,
  LayoutDashboard,
  Sparkles,
  AlertCircle,
  NotebookPen,
  AudioLines,
  Wallet,
  Library,
} from 'lucide-react'

interface NavItem {
  link?: string
  Icon?: React.ComponentType<{ size?: string | number }>
  title: string
  id?: number
  children?: NavItem[]
  showIf?: (roles: string[]) => boolean
  isNew?: boolean
  external?: boolean
}

interface ModernSidebarLayoutProps {
  children: ReactNode
  meta: { robots: string }
}

const navData: {
  teacher: NavItem[]
  student: NavItem[]
} = {
  teacher: [
    { link: '/user/permanent', Icon: SlCamrecorder, title: 'My Classroom', id: 3 },
    { link: '/subscribe', Icon: SlCreditCard, title: 'Subscribe', id: 6 },
    { link: '/user/products', Icon: CreditCard, title: 'Receive Payments', id: 18 },
    { link: '/user/highlights', Icon: MdHighlight, title: 'Reels', id: 13 },
    { link: '/notes', Icon: SlNote, title: 'Notes', id: 5, showIf: (roles: string[]) => roles.length === 0 || roles.includes('Music Tutor') },
    {
      Icon: SlPlaylist,
      title: 'Audio Library',
      id: 7,
      showIf: (roles: string[]) => roles.length === 0 || roles.includes('Music Tutor'),
      children: [
        { link: '/playlists', title: 'Playlists', id: 8 },
        { link: '/files', title: 'Files', id: 9 },
      ],
    },
    { link: '/recordings', Icon: MdVideoLibrary, title: 'Recordings', id: 12 },
    { link: '/user/transcriptions', Icon: MdOutlineTranscribe, title: 'Transcriptions', id: 17 },
  ],
  student: [
    { link: '/student/teacher', Icon: FaChalkboardTeacher, title: 'Teacher', id: 20 },
    { link: '/student/calendar', Icon: SlCalender, title: 'Calendar', id: 22 },
    { link: '/student/notes', Icon: SlNotebook, title: 'Notes', id: 28 },
  ],
}

const routeMeta: Record<string, { title: string; subtitle: string }> = {
  '/user/permanent': {
    title: 'Teacher Dashboard',
    subtitle: 'Run your classroom, share one lesson link, and stay ready to teach.',
  },
  '/files': {
    title: 'Files',
    subtitle: 'Keep backing tracks and references organized for faster lesson prep.',
  },
  '/playlists': {
    title: 'Playlists',
    subtitle: 'Build reusable sets for warmups, drills, and performance work.',
  },
  '/my-students': {
    title: 'Students',
    subtitle: 'Manage your roster and keep each learner connected to your classroom.',
  },
  '/recordings': {
    title: 'Recordings',
    subtitle: 'Review lesson capture and reuse moments that matter.',
  },
  '/notes': {
    title: 'Lesson Notes',
    subtitle: 'Track feedback, goals, and progress between lessons.',
  },
  '/user/products': {
    title: 'Payments',
    subtitle: 'Package your teaching offers and share purchase links with confidence.',
  },
  '/user/highlights': {
    title: 'Highlights',
    subtitle: 'Turn lesson moments into short promotional clips and reels.',
  },
  '/account': {
    title: 'Account',
    subtitle: 'Update your details, billing, and preferences.',
  },
  '/subscribe': {
    title: 'Subscription',
    subtitle: 'Upgrade when the classroom workflow has already proven its value.',
  },
}

const pageMotion = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: 'easeOut' },
}

const ModernSidebarLayoutComponent = ({ children, meta }: ModernSidebarLayoutProps) => {
  const router = useRouter()
  const { actions, state } = useAuthContext()
  const [copiedClassroomLink, setCopiedClassroomLink] = useState(false)

  const sessionStatsRequest = useRequest({
    url: '/lms/session/stats/',
  })
  const sessionStats = state?.userType === 'teacher' ? sessionStatsRequest.data : undefined

  const { data: subscriptionStatus } = useRequest({
    url: '/main/subscription-status',
  })

  const isSubscribed = state?.info?.subsrciption || false

  const getSubscriptionInfo = () => {
    if (isSubscribed) {
      return { status: 'active', showPortalButton: false, message: null }
    }

    if (!subscriptionStatus) {
      return { status: 'loading', showPortalButton: false, message: null }
    }

    const { status, stripe_customer_portal_available } = subscriptionStatus

    switch (status) {
    case 'never_subscribed':
      return { status: 'never_subscribed', showPortalButton: false, message: null }
    case 'expired_payment_failed':
      return {
        status: 'payment_failed',
        showPortalButton: stripe_customer_portal_available,
        message: 'Payment failed. Please update your payment method.',
      }
    case 'expired_canceled':
      return {
        status: 'expired_canceled',
        showPortalButton: false,
        message: 'Your subscription has been canceled.',
      }
    default:
      return {
        status: 'expired_other',
        showPortalButton: stripe_customer_portal_available,
        message: 'Your subscription has expired.',
      }
    }
  }

  const subscriptionInfo = getSubscriptionInfo()

  const handleCustomerPortal = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/main/stripe-customer-portal`, {
        method: 'GET',
        headers: {
          Authorization: `Token ${state.key}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        window.open(data.url, '_blank')
      }
    } catch (error) {
      console.error('Error accessing customer portal:', error)
    }
  }

  const userData = navData[state?.userType as keyof typeof navData] || []
  const filteredUserData = userData.filter((item) => !(item.title === 'Subscribe' && isSubscribed))

  const navGroups = useMemo(() => {
    if (state?.userType === 'student') {
      return [{ title: 'Navigation', items: filteredUserData }]
    }

    const groups = []
    const classroomItems = filteredUserData.filter((item) =>
      ['My Classroom', 'Notes', 'Audio Library'].includes(item.title))
    if (classroomItems.length) groups.push({ title: 'Start Teaching', items: classroomItems })

    const contentItems = filteredUserData.filter((item) =>
      ['Receive Payments', 'Reels', 'Subscribe'].includes(item.title))
    if (contentItems.length) groups.push({ title: 'Grow & Sell', items: contentItems })

    const recordsItems = filteredUserData.filter((item) =>
      ['Recordings', 'Transcriptions'].includes(item.title))
    if (recordsItems.length) groups.push({ title: 'Library & History', items: recordsItems })

    return groups
  }, [filteredUserData, state?.userType])

  const avatarMenuItems: NavItem[] = [
    { link: 'https://calendar.app.google/aQVS81sYMhgygcEd9', Icon: SlCalender, title: 'Call with Founder', id: 14, external: true },
    { link: '/account', Icon: SlUser, title: 'My Account', id: 15 },
    { link: '/user/sales', Icon: CreditCard, title: 'Transactions', id: 16 },
    { link: '/history', Icon: SlNotebook, title: 'History', id: 18 },
    { link: 'https://enchanted-curler-57b.notion.site/App-Updates-c12e1e2256f64546a0964068b0388d8b', Icon: SlRocket, title: "What's New", id: 19, external: true },
  ]

  const activeRouteMeta = routeMeta[router.pathname] || {
    title: state?.userType === 'student' ? 'Student Workspace' : 'Dashboard',
    subtitle: state?.userType === 'student'
      ? 'Keep your lessons, teacher communication, and notes in one place.'
      : 'Everything important for your next lesson is organized here.',
  }

  const teacherName = state?.me?.first_name || 'Teacher'
  const roomName = state?.info?.room_name
  const classroomLink = typeof window !== 'undefined' && roomName
    ? `${window.location.origin}/room/${roomName}`
    : ''

  const handleCopyClassroomLink = async () => {
    if (!classroomLink) return
    await navigator.clipboard.writeText(classroomLink)
    setCopiedClassroomLink(true)
    window.setTimeout(() => setCopiedClassroomLink(false), 1800)
  }

  const statsCards = [
    {
      label: '7-day teaching',
      value: `${sessionStats?.hours_last_7_days || 0}h`,
      icon: Clock3,
    },
    {
      label: '30-day teaching',
      value: `${sessionStats?.hours_last_30_days || 0}h`,
      icon: Calendar,
    },
    {
      label: subscriptionInfo.status === 'active' ? 'Subscription' : 'Room minutes',
      value: subscriptionInfo.status === 'active'
        ? 'Active'
        : `${state?.info?.room_limit_left || 0} min`,
      icon: subscriptionInfo.status === 'active' ? Sparkles : AlertCircle,
    },
  ]

  const pageContext = useMemo(() => {
    if (state?.userType !== 'teacher') {
      return null
    }

    if (router.pathname === '/user/permanent') {
      return {
        icon: AudioLines,
        eyebrow: 'Activation',
        title: roomName ? `Room ready: ${roomName}` : 'Room setup in progress',
        description: 'Use this space to test audio, polish your classroom link, and stay ready for repeat lessons.',
        className: 'border-[#f4d7df] bg-[#fff7f9]',
      }
    }

    if (router.pathname === '/user/products' || router.pathname === '/user/sales' || router.pathname === '/subscribe') {
      return {
        icon: Wallet,
        eyebrow: 'Business',
        title: 'Teaching revenue',
        description: 'Keep payments and plan details close to the classroom, but secondary to teaching actions.',
        className: 'border-[#e8e7fb] bg-[#f8faff]',
      }
    }

    if (router.pathname === '/notes' || router.pathname === '/playlists' || router.pathname === '/files') {
      return {
        icon: NotebookPen,
        eyebrow: 'Teaching tools',
        title: 'Lesson prep',
        description: 'Organize notes and supporting material so each lesson starts without extra setup work.',
        className: 'border-[#f4d7df] bg-[#fff7f9]',
      }
    }

    return {
      icon: Library,
      eyebrow: 'Workspace',
      title: 'Teacher studio',
      description: 'Everything important for the next lesson is grouped here in one dependable workspace.',
      className: 'border-[#e8e7fb] bg-[#f8faff]',
    }
  }, [roomName, router.pathname, state?.userType])

  const renderNavLink = (item: NavItem) => {
    const IconComponent = item.Icon
    const isActive = !!item.link && (router.pathname === item.link || router.pathname.startsWith(`${item.link}/`))
    const hasChildren = item.children && item.children.length > 0

    if (hasChildren) {
      return (
        <SidebarMenuItem key={item.id}>
          <SidebarMenuButton className="rounded-2xl">
            {IconComponent && <IconComponent size={16} />}
            <span>{item.title}</span>
            <ChevronRight className="ml-auto h-4 w-4" />
          </SidebarMenuButton>
          <SidebarMenuSub>
            {item.children?.map((child) => {
              const childActive = !!child.link && router.pathname === child.link
              return (
                <SidebarMenuSubItem key={child.id}>
                  <SidebarMenuSubButton asChild data-active={childActive}>
                    <Link href={child.link || '#'}>
                      <span>{child.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </SidebarMenuItem>
      )
    }

    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          className={
            item.title === 'My Classroom'
              ? 'rounded-2xl bg-[#fff7f9] text-[#211e57] hover:bg-[#fff1f4]'
              : item.title === 'Subscribe'
                ? 'rounded-2xl bg-primary text-white hover:bg-primary/90'
                : 'rounded-2xl'
          }
        >
          <Link href={item.link || '#'}>
            {IconComponent && <IconComponent size={16} />}
            <span>{item.title}</span>
            {item.isNew && (
              <Badge variant="secondary" className="ml-auto">
                New
              </Badge>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <div className="min-h-screen">
      <Head customMeta={meta} />
      <PhoneNumberModal />
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-transparent px-2 py-2 md:px-4 md:py-4">
          <Sidebar
            variant="floating"
            className="border-0 md:p-0"
          >
            <div className="teacher-shell-glow flex h-full flex-col rounded-[30px] border border-white/80 bg-white/92 shadow-[0_18px_50px_rgba(33,30,87,0.12)] backdrop-blur-xl">
              <SidebarHeader className="border-b border-[#ece8f6] px-4 py-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-[#fff4f6] p-2.5 text-[#fd4367]">
                      <LayoutDashboard className="h-5 w-5" />
                    </div>
                    <div>
                      <Logo />
                      <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#6b7280]">
                        Teacher studio
                      </p>
                    </div>
                  </div>
                  <SidebarTrigger className="md:hidden" />
                </div>
              </SidebarHeader>

              <SidebarContent className="px-2 pb-4">
                <ScrollArea className="h-full">
                  {state?.userType === 'teacher' && (
                    <SidebarGroup className="px-2 py-3">
                      <div className="teacher-soft-panel p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#fd4367]">
                              Ready to teach
                            </p>
                            <h2 className="mt-2 text-xl">Hi, {teacherName}</h2>
                          </div>
                          <Badge variant="outline">Music lessons</Badge>
                        </div>

                        <div className="mt-4 grid gap-2">
                          {statsCards.map((stat) => (
                            <div key={stat.label} className="rounded-2xl bg-white/90 p-3 shadow-[0_8px_20px_rgba(33,30,87,0.05)]">
                              <div className="flex items-center gap-2 text-[#211e57]">
                                <stat.icon className="h-4 w-4 text-[#fd4367]" />
                                <span className="text-sm font-semibold">{stat.value}</span>
                              </div>
                              <p className="mt-1 text-xs uppercase tracking-[0.14em] text-[#6b7280]">{stat.label}</p>
                            </div>
                          ))}
                        </div>

                        {subscriptionInfo.status === 'payment_failed' && (
                          <div className="mt-4 rounded-2xl border border-[#ffd6c8] bg-[#fff4ef] p-3 text-sm text-[#8d4f35]">
                            <p className="font-semibold">{subscriptionInfo.message}</p>
                            {subscriptionInfo.showPortalButton && (
                              <Button onClick={handleCustomerPortal} size="sm" className="mt-3">
                                Update payment
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </SidebarGroup>
                  )}

                  {navGroups.map((group) => (
                    <SidebarGroup key={group.title} className="px-2 py-1">
                      <SidebarGroupLabel className="px-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b8fa3]">
                        {group.title}
                      </SidebarGroupLabel>
                      <SidebarGroupContent>
                        <SidebarMenu className="gap-1.5">
                          {group.items.map((item) => {
                            if (item.showIf && !item.showIf([])) return null
                            return renderNavLink(item)
                          })}
                        </SidebarMenu>
                      </SidebarGroupContent>
                    </SidebarGroup>
                  ))}

                  <SidebarGroup className="px-2 py-1">
                    <SidebarGroupLabel className="px-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b8fa3]">
                      Account
                    </SidebarGroupLabel>
                    <SidebarGroupContent>
                      <SidebarMenu className="gap-1.5">
                        {avatarMenuItems.map((item) => (
                          <SidebarMenuItem key={item.id || item.title}>
                            <SidebarMenuButton
                              asChild
                              className="rounded-2xl"
                              isActive={!item.external && !!item.link && router.pathname === item.link}
                            >
                              {item.external ? (
                                <a href={item.link} target="_blank" rel="noreferrer">
                                  {item.Icon && <item.Icon size={16} />}
                                  <span>{item.title}</span>
                                  <ExternalLink className="ml-auto h-4 w-4" />
                                </a>
                              ) : (
                                <Link href={item.link || '#'}>
                                  {item.Icon && <item.Icon size={16} />}
                                  <span>{item.title}</span>
                                </Link>
                              )}
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}

                        <SidebarMenuItem>
                          <SidebarMenuButton
                            onClick={() => {
                              window.localStorage.clear()
                              actions.setKey('')
                              window.setTimeout(() => {
                                router.push('/')
                              }, 0)
                            }}
                            className="rounded-2xl text-rose-600 hover:text-rose-700"
                          >
                            <SlLogout size={16} />
                            <span>Logout</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        <SidebarMenuItem>
                          <SidebarMenuButton
                            onClick={() => {
                              window.location.href = '/install_pwa'
                            }}
                            className="rounded-2xl"
                          >
                            <MdInstallDesktop size={16} />
                            <span>Install Desktop App</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </SidebarMenu>
                    </SidebarGroupContent>
                  </SidebarGroup>
                </ScrollArea>
              </SidebarContent>
              <SidebarRail />
            </div>
          </Sidebar>

          <div className="flex min-h-screen flex-1 flex-col">
            <header className="sticky top-0 z-20 px-2 pb-3 pt-2 md:px-5">
              <motion.div
                {...pageMotion}
                className="teacher-panel flex flex-col gap-4 px-4 py-4 md:px-6 md:py-5 lg:flex-row lg:items-center lg:justify-between"
              >
                <div className="flex items-start gap-3">
                  <SidebarTrigger className="mt-1 hidden md:inline-flex" />
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#fd4367]">
                      {state?.userType === 'student' ? 'Student workspace' : 'Teacher dashboard'}
                    </p>
                    <h1 className="mt-2 text-3xl md:text-4xl">{activeRouteMeta.title}</h1>
                    <p className="mt-2 max-w-2xl text-sm leading-6 text-[#5b6175] md:text-[15px]">
                      {activeRouteMeta.subtitle}
                    </p>
                  </div>
                </div>

                <div className="grid gap-3 lg:grid-cols-[minmax(0,320px)_auto]">
                  {state?.userType === 'teacher' && (
                    <>
                      {pageContext && (
                        <div className={`rounded-[22px] border px-4 py-3 ${pageContext.className}`}>
                          <div className="flex items-start gap-3">
                            <div className="rounded-2xl bg-white/80 p-2.5 text-[#fd4367]">
                              <pageContext.icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#fd4367]">
                                {pageContext.eyebrow}
                              </p>
                              <p className="mt-1 text-sm font-semibold text-[#211e57]">{pageContext.title}</p>
                              <p className="mt-1 text-xs leading-5 text-[#6b7280]">
                                {pageContext.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col gap-3 sm:flex-row">
                        {router.pathname !== '/user/permanent' && (
                          <Button asChild size="lg" className="justify-between">
                            <Link href="/user/permanent">
                              <span>Open classroom hub</span>
                              <ArrowUpRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}

                        {classroomLink && (
                          <Button variant="outline" size="lg" onClick={handleCopyClassroomLink} className="justify-between bg-white">
                            <span>{copiedClassroomLink ? 'Link copied' : 'Copy class link'}</span>
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </header>

            <main className="flex-1 px-2 pb-4 md:px-5 md:pb-5">
              <motion.div
                {...pageMotion}
                transition={{ ...pageMotion.transition, delay: 0.08 }}
                className="mx-auto w-full max-w-[1440px]"
              >
                {children}
              </motion.div>
            </main>
          </div>
        </div>
      </SidebarProvider>
      <ToastContainer />
    </div>
  )
}

const ModernSidebarLayout = ProtectedPage(ModernSidebarLayoutComponent)

export default ModernSidebarLayout
